import { useEffect, useState, useRef, useCallback } from 'react';
import { Bell, X, AlertCircle, CheckCircle, Info, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * NotificationBell — top-bar bell icon + dropdown for in-app alerts.
 *
 * Tenant isolation:
 *   - Backend endpoint `/api/notifications` always scopes to
 *     `current_user.user_id` — a user can ONLY see their own alerts.
 *   - Optional `workspaceId` prop narrows the list further to a single
 *     workspace context (rendered when used inside a workspace shell).
 *
 * Polling cadence: every 60s. Single source of truth for `unread_count`
 * is the backend — we read it directly from the list response so the
 * glow badge can't drift out of sync.
 */
export default function NotificationBell({
  isDarkMode,
  workspaceId,
  mutedTextClass,
  hoverBgClass,
}) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      const resp = await api.get('/api/notifications', { params });
      setItems(resp.data?.items || []);
      setUnreadCount(resp.data?.unread_count || 0);
    } catch (e) {
      // Silent — bell is a passive surface, don't surface its own errors
    }
  }, [api, workspaceId]);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Click-outside closer
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (notification_id) => {
    try {
      await api.post(`/api/notifications/${notification_id}/read`);
      setItems((prev) => prev.map((n) =>
        n.notification_id === notification_id
          ? { ...n, read_at: new Date().toISOString() }
          : n,
      ));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {}
  };

  const handleMarkAllRead = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      await api.post('/api/notifications/mark-all-read', null, { params });
      const now = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (notification_id) => {
    try {
      await api.delete(`/api/notifications/${notification_id}`);
      setItems((prev) => prev.filter((n) => n.notification_id !== notification_id));
      // If the dismissed one was unread, decrement the badge.
      const dismissed = items.find((n) => n.notification_id === notification_id);
      if (dismissed && !dismissed.read_at) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {}
  };

  const handleClickNotification = async (n) => {
    if (!n.read_at) {
      await handleMarkRead(n.notification_id);
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const severityIcon = (severity) => {
    if (severity === 'error') return <AlertCircle className="text-red-400" size={16} />;
    if (severity === 'warning') return <AlertTriangle className="text-amber-400" size={16} />;
    if (severity === 'success') return <CheckCircle className="text-emerald-400" size={16} />;
    return <Info className="text-blue-400" size={16} />;
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  };

  const hasUnread = unreadCount > 0;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative p-2 rounded-lg ${mutedTextClass} ${hoverBgClass} transition-colors`}
        data-testid="notification-bell-btn"
        title={hasUnread ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'}
        aria-label="Notifications"
      >
        {/* Bell with glow animation when there are unread items */}
        <Bell
          size={18}
          className={hasUnread ? 'animate-pulse text-amber-400' : ''}
        />
        {hasUnread && (
          <>
            {/* Glowing ring around bell — subtle pulse */}
            <span
              className="absolute inset-0 rounded-lg ring-2 ring-amber-400/40 animate-pulse pointer-events-none"
              aria-hidden="true"
            />
            {/* Unread count badge */}
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-[10px] font-bold text-white flex items-center justify-center"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          className={`absolute right-0 mt-2 w-96 max-h-[480px] overflow-hidden rounded-xl shadow-2xl border z-50 ${
            isDarkMode
              ? 'bg-slate-900 border-slate-700'
              : 'bg-white border-slate-200'
          }`}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-slate-700' : 'border-slate-200'
          }`}>
            <div className="flex items-center gap-2">
              <Bell size={14} className={mutedTextClass} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Notifications
              </span>
              {hasUnread && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                  {unreadCount} new
                </span>
              )}
            </div>
            {hasUnread && (
              <button
                onClick={handleMarkAllRead}
                disabled={loading}
                className="text-xs text-teal-400 hover:text-teal-300 transition-colors disabled:opacity-40"
                data-testid="notification-mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Items */}
          <div className="overflow-y-auto max-h-[400px]">
            {items.length === 0 ? (
              <div className={`px-4 py-12 text-center ${mutedTextClass}`}>
                <Bell size={24} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs mt-1 opacity-70">We'll let you know when something needs your attention.</p>
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.notification_id}
                  data-testid={`notification-item-${n.notification_id}`}
                  className={`group px-4 py-3 border-b transition-colors cursor-pointer ${
                    isDarkMode ? 'border-slate-800 hover:bg-slate-800/60' : 'border-slate-100 hover:bg-slate-50'
                  } ${n.read_at ? 'opacity-60' : ''}`}
                  onClick={() => handleClickNotification(n)}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5">{severityIcon(n.severity)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          {n.title}
                        </p>
                        <div className="flex items-center gap-1">
                          {!n.read_at && (
                            <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" aria-label="Unread" />
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDismiss(n.notification_id); }}
                            className={`opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity p-0.5 rounded ${mutedTextClass}`}
                            data-testid={`notification-dismiss-${n.notification_id}`}
                            title="Dismiss"
                            aria-label="Dismiss notification"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                      <p className={`text-xs mt-0.5 ${mutedTextClass} line-clamp-3`}>
                        {n.body}
                      </p>
                      {n.type === 'version_update' && (
                        <button
                          data-testid={`notification-refresh-${n.notification_id}`}
                          onClick={(e) => { e.stopPropagation(); window.location.reload(); }}
                          className="mt-2 flex items-center gap-1.5 bg-teal-500 text-black px-3 py-1 rounded-full text-xs font-semibold hover:bg-teal-400 transition-colors"
                        >
                          <RefreshCw size={12} /> Refresh now
                        </button>
                      )}
                      {(n.kind === 'video_review' || n.type === 'video_review') && n.link && (
                        <button
                          data-testid={`notification-review-video-${n.notification_id}`}
                          onClick={(e) => { e.stopPropagation(); handleClickNotification(n); }}
                          className="mt-2 flex items-center gap-1.5 bg-teal-500 text-black px-3 py-1 rounded-full text-xs font-semibold hover:bg-teal-400 transition-colors"
                        >
                          Review & approve <ChevronRight size={12} />
                        </button>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className={`text-[10px] ${mutedTextClass} opacity-70`}>
                          {formatRelativeTime(n.created_at)}
                        </span>
                        {n.link && (
                          <ChevronRight size={12} className={`${mutedTextClass} opacity-50 group-hover:opacity-100`} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
